import React from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import MuiLink from '@mui/material/Link'
import Card from '@mui/material/Card'
import CardActionArea from '@mui/material/CardActionArea'
import CardActions from '@mui/material/CardActions'
import CardContent from '@mui/material/CardContent'
import CardMedia from '@mui/material/CardMedia'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import Rating from '@mui/material/Rating'
import Collapse from '@mui/material/Collapse'
import LoadingButton from '@mui/lab/LoadingButton'
import Scale from '@mui/icons-material/Scale'
import LocalLaundryService from '@mui/icons-material/LocalLaundryService'
import BlurLinear from '@mui/icons-material/BlurLinear'
import VerifiedIcon from '@mui/icons-material/Verified'

import BeanIcon from '../../svgicons/bean'
import ProfileTypeChip from '../profiles/profileTypeChip'
import { Profile } from '../../types/profile'
import ProfileEditorChart from '../charts/profileEditorChart'
import { SeriesType } from '../../types/enums/seriesType'
import { getValueSuffix } from '../../utils/graphUtils'
import { getMainProfileParam, getProfileSeries, getXaxisLimits } from '../../utils/profileEditorUtils'
import { ProfileMetadataFilter } from '../../types/profileMetadataFilter'
import { patch } from '../../utils/service'
import Alert from '../alert/alert'
import { isPounds } from '../../utils/localStorageUtils'
import ProfileLibraryCardChart from '../charts/profileLibraryCardChart'

interface ProfileLibraryCardProps {
  customerUrl: string
  filterConfig: Array<ProfileMetadataFilter>
  profile: Profile
  selectedMachineSlug: string
}

// values will be shown show values in Fahrenheit / ounces depending on localStorage settings.
const ProfileLibraryCard = (props: ProfileLibraryCardProps): JSX.Element => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [isExpanded, setIsExpanded] = React.useState<boolean>(false)
  const [isAddingToLibrary, setIsAddingToLibrary] = React.useState<boolean>(false)
  const [hasAddToLibrarySuccess, setHasAddToLibrarySuccess] = React.useState<boolean>(false)
  const [hasAddToLibraryError, setHasAddToLibraryError] = React.useState<boolean>(false)

  const [minTime, maxTime] = getXaxisLimits(props.profile.profile_type, 0)

  const getFilterIcon = (icon: string): JSX.Element | null => {
    if (icon === 'origin') {
      return <BeanIcon />
    } else if (icon === 'process') {
      return <LocalLaundryService />
    } else if (icon === 'density') {
      return <BlurLinear />
    } else if (icon === 'scale') {
      return <Scale />
    }
    return null
  }

  const renderInfoElement = (key: string, label: string, value: string, icon: string): JSX.Element => {
    return (
      <Stack key={key} direction="row" spacing={0.5} sx={{ flex: 1 }}>
        {value && (
          <>
            <Tooltip title={label}>
              <Stack sx={{ color: 'primary.main' }}>{getFilterIcon(icon)}</Stack>
            </Tooltip>
            <Typography>{value}</Typography>
          </>
        )}
      </Stack>
    )
  }

  const renderRating = (): JSX.Element => {
    const ratings = props.profile.ratings || []
    const avgRating =
      props.profile.ratings && props.profile.ratings.length > 0
        ? ratings.reduce((sum, current) => sum + current.rating, 0) / ratings.length
        : 0
    const downloads = (props.profile.downloads || 0) + (hasAddToLibrarySuccess ? 1 : 0)
    return (
      <Stack direction="row" alignItems="flex-end" spacing={0.5}>
        <Typography>{avgRating.toFixed(2) || 'N/A'}</Typography>
        <Rating
          sx={{ color: 'primary.main', '&.Mui-disabled': { opacity: 1 } }}
          disabled
          value={avgRating}
          precision={0.1}
        />
        <Typography variant="caption">
          {[
            t('profilelibrary.ratings').replace('{0}', `${ratings.length || 0}`),
            t('profilelibrary.downloads').replace('{0}', `${downloads}`),
          ].join(', ')}
        </Typography>
      </Stack>
    )
  }

  const clickAddToLibrary = (): void => {
    if (!isProfileInLibrary()) {
      addToLibrary()
    } else {
      navigate(`/machine/${props.selectedMachineSlug}/profiles/${props.profile.id}`)
    }
  }

  const addToLibrary = (): void => {
    setIsAddingToLibrary(true)
    setHasAddToLibraryError(false)
    patch<{ import_profile: string }, {}>(props.customerUrl, {
      import_profile: props.profile.url,
    })
      .then(() => {
        setIsAddingToLibrary(false)
        setHasAddToLibrarySuccess(true)
      })
      .catch(() => {
        setIsAddingToLibrary(false)
        setHasAddToLibraryError(true)
      })
  }

  const getImageString = (): string | undefined => {
    return props.profile.metadata_image
      ? `data:${props.profile.metadata_image.b64_img_type};base64,${props.profile.metadata_image.b64_img_data}`
      : undefined
  }

  const isProfileInLibrary = (): boolean => {
    return (
      hasAddToLibrarySuccess ||
      props.profile.customer_has_shared_profile ||
      props.profile.customer === props.customerUrl
    )
  }

  const getProfileNotes = (showAll: boolean): JSX.Element => {
    const notes = props.profile.notes
    const shortLength = 25

    let output = ''
    let link = false

    if (notes == null) {
      return <></>
    }

    if (showAll) {
      output = notes
    } else {
      const words = notes.split(' ')
      if (words.length > shortLength) {
        output = words.slice(0, shortLength - 2).join(' ') + ' '
        link = true
      } else {
        output = notes
      }
    }

    return (
      <>
        {output}
        {link && <MuiLink onClick={() => {}}>{t('profilelibrary.showmore')}</MuiLink>}
      </>
    )
  }

  return (
    <Card elevation={3}>
      <CardActionArea onClick={() => setIsExpanded((oldValue) => !oldValue)}>
        <Stack direction={{ xs: 'column', sm: 'row' }}>
          <Stack sx={{ p: 1 }}>
            {props.profile.metadata_image ? (
              <CardMedia component="img" sx={{ width: 150, alignSelf: 'center' }} image={getImageString()} />
            ) : (
              <ProfileLibraryCardChart
                series={getProfileSeries(props.profile).find(
                  (x) => x.param === getMainProfileParam(props.profile.profile_type)
                )}
              />
            )}
          </Stack>
          <Stack sx={{ flex: 1 }}>
            <CardContent>
              <Typography variant="h5">
                {props.profile.name}
                {props.profile.is_verified && (
                  <Tooltip title={t('profilelibrary.verifiedprofile')} color="primary">
                    <VerifiedIcon sx={{ verticalAlign: 'middle', ml: 1 }} />
                  </Tooltip>
                )}
              </Typography>
              <Typography variant="body2">
                {t('profilelibrary.createdby').replace('{0}', props.profile.author_name)}
              </Typography>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 1, md: 3 }} sx={{ mt: 1.5 }}>
                <Typography sx={{ whiteSpace: 'pre-wrap', flex: 3 }}>{getProfileNotes(isExpanded)}</Typography>
                <Stack sx={{ flex: 1, alignItems: 'flex-start' }}>
                  <ProfileTypeChip profile={props.profile} />
                </Stack>
                {renderInfoElement(
                  `${props.profile.id}-weight`,
                  t('profilelibrary.weight'),
                  props.profile.batch_weight
                    ? `${props.profile.batch_weight.toFixed(isPounds() ? 2 : 0)}${getValueSuffix(SeriesType.WEIGHT)}`
                    : '',
                  'scale'
                )}
                {props.filterConfig.map((x) => {
                  const filterValues = x.filter_values
                    .filter((y) => (props.profile.selected_filters || []).map((z) => z.id).indexOf(y.id) > -1)
                    .map((y) => y.label)
                    .join(', ')
                  return renderInfoElement(`${props.profile.id}-${x.id}`, x.name, filterValues, x.icon)
                })}
              </Stack>
              <Stack>{renderRating()}</Stack>
            </CardContent>
          </Stack>
        </Stack>
      </CardActionArea>
      <Collapse
        in={isExpanded}
        timeout={300}
        unmountOnExit
        addEndListener={(node: HTMLElement) => {
          if (isExpanded) {
            setTimeout(() => {
              node.scrollIntoView({ behavior: 'smooth', block: 'end' })
            }, 300)
          }
        }}
      >
        <CardContent>
          <ProfileEditorChart
            isReadonlyProfile={true}
            autoDropTime={0}
            minX={minTime}
            maxX={maxTime}
            moveIntervalX={0}
            selectedSeries={getMainProfileParam(props.profile.profile_type)}
            profileType={props.profile.profile_type}
            seriesConfig={getProfileSeries(props.profile)}
            compareSeries={[]}
            compareLogEvents={null}
            selectedPointIndexes={[]}
            onDropPoint={() => {
              /** */
            }}
            onCreatePoint={() => {
              /** */
            }}
            setCurrentSeries={() => {
              /** */
            }}
            selectPoint={() => {
              /* */
            }}
            unselectPoint={() => {
              /* */
            }}
            keyboardShortcutButton={<></>}
            user={null}
          />
          {hasAddToLibraryError && <Alert severity="error">{t('profilelibrary.importprofileerror')}</Alert>}
          {hasAddToLibrarySuccess && (
            <Alert severity="success">
              {t('profilelibrary.importprofilesuccess')}{' '}
              <MuiLink
                component={Link}
                to={`/machine/${props.selectedMachineSlug}/profiles/${props.profile.id}`}
                state={{ showAddToLibrarySuccess: true }}
              >
                {t('profilelibrary.viewandassignprofile')}
              </MuiLink>
            </Alert>
          )}
          <CardActions sx={{ justifyContent: 'flex-end' }}>
            {props.customerUrl && (
              <LoadingButton
                variant="contained"
                loading={isAddingToLibrary}
                onClick={(e) => {
                  e.stopPropagation()
                  clickAddToLibrary()
                }}
              >
                {isProfileInLibrary() ? t('profilelibrary.viewprofile') : t('profilelibrary.importprofile')}
              </LoadingButton>
            )}
          </CardActions>
        </CardContent>
      </Collapse>
    </Card>
  )
}

export default ProfileLibraryCard
